import {
  AccountBalanceWalletOutlined,
  ApartmentOutlined,
  AssessmentOutlined,
  DashboardOutlined,
  DescriptionOutlined,
  EventAvailableOutlined,
  GroupOutlined,
  LockOutlined,
  NoteAltOutlined,
  SecurityOutlined,
  SettingsOutlined,
  SwapHorizOutlined,
} from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { NavLink, Navigate, Outlet, useLocation } from 'react-router';
import {
  CurrentSessionSchema,
  SwitchOrganizationRequestSchema,
  type Permission,
} from '@nlc/contracts';

import { apiRequest, ApiError } from '../api/client.js';
import { hasPermission, sessionQuery } from '../auth/session.js';
import { ErrorState, LoadingState } from '../components/states.js';
import { brand } from '../brand.js';
import logoLight from '../assets/suncoast-logo-light.svg';
import type { CurrentSession, Feature } from '@nlc/contracts';

const drawerWidth = 264;

/** Items with no `feature` are structural and always enabled. */
function isEnabled(session: CurrentSession, item: Pick<NavigationItem, 'feature'>): boolean {
  return item.feature === undefined || session.features.includes(item.feature);
}

/**
 * `permission` is authorization; `feature` is whether this deployment bought the
 * area at all. Both must pass for an item to appear, and `feature` also gates
 * the route itself below — hiding a link while leaving the page reachable is
 * theatre, not configuration.
 *
 * Items with no `feature` are structural and always present.
 */
type NavigationItem = {
  label: string;
  to: string;
  icon: ReactNode;
  permission: Permission;
  feature?: Feature;
  group: NavigationGroup;
};

/**
 * What people read every day, the month-end routine, and setting the portal up.
 * Grouped since 2026-10-01: thirteen flat entries, half of them administration,
 * read as one undifferentiated list. An owner sees only the first group.
 */
type NavigationGroup = 'views' | 'month-end' | 'admin';
const GROUP_TITLES: Record<NavigationGroup, string | null> = {
  views: null,
  'month-end': 'Month end',
  admin: 'Administration',
};
const navigation: NavigationItem[] = [
  {
    // Stephanie Friedl, 2026-10-20. The page has led with occupancy since the
    // summary tiles came off; "Reservations" named the Campspot half, which is
    // now the smaller part of what is on it. Route stays "/" -- renaming the
    // path would break every saved view, which stores a URL verbatim.
    label: 'Occupancy',
    to: '/',
    icon: <EventAvailableOutlined />,
    permission: 'reservations.read',
    feature: 'reservations',
    group: 'views',
  },
  {
    label: 'Dashboard',
    to: '/dashboard',
    icon: <DashboardOutlined />,
    permission: 'dashboard.read',
    feature: 'dashboard',
    group: 'views',
  },
  {
    label: 'Documents',
    to: '/documents',
    icon: <DescriptionOutlined />,
    permission: 'documents.read',
    feature: 'documents',
    group: 'views',
  },
  {
    label: 'Reports',
    to: '/reports',
    icon: <AssessmentOutlined />,
    permission: 'reports.read',
    feature: 'reports',
    group: 'views',
  },
  {
    // Notes are written from the report or the dashboard, against the number
    // that prompted them. This screen is the housekeeping: find what you wrote,
    // remove what no longer applies. Reading needs only `reports.read`, but
    // there is nothing to do here without the ability to write.
    label: 'Notes',
    to: '/notes',
    icon: <NoteAltOutlined />,
    permission: 'reports.note',
    feature: 'reports',
    group: 'month-end',
  },
  {
    // Admin workflow, not a view. `reports.reconcile` is administrator-only, so
    // this never appears for an owner or an investor.
    label: 'Month close',
    to: '/month-close',
    icon: <LockOutlined />,
    permission: 'reports.reconcile',
    feature: 'reports',
    group: 'month-end',
  },
  {
    // Each park confirms its own moves and closes the month (Spark 287230), so a
    // property manager has this as well as an administrator.
    label: 'Move-ins & move-outs',
    to: '/moves',
    icon: <SwapHorizOutlined />,
    permission: 'months.close',
    feature: 'reports',
    group: 'month-end',
  },
  {
    label: 'Members',
    to: '/admin/members',
    icon: <GroupOutlined />,
    permission: 'memberships.read',
    group: 'admin',
  },
  {
    label: 'Roles & audit',
    to: '/admin/security',
    icon: <SecurityOutlined />,
    permission: 'roles.read',
    group: 'admin',
  },
  {
    // Configuration rather than reporting, so it sits behind
    // `organizations.manage`: a manager must not be able to widen their own view.
    // The view itself is a report, under Reports.
    label: 'Manager budget lines',
    to: '/admin/budget-lines',
    icon: <AccountBalanceWalletOutlined />,
    permission: 'organizations.manage',
    feature: 'reports',
    group: 'admin',
  },
  {
    label: 'Integrations',
    to: '/admin/integrations',
    icon: <ApartmentOutlined />,
    permission: 'integrations.read',
    feature: 'integrations',
    group: 'admin',
  },
  {
    label: 'Organization',
    to: '/admin/organization',
    icon: <SettingsOutlined />,
    permission: 'organizations.read',
    feature: 'organization',
    group: 'admin',
  },
];

export default function AppShell(): React.JSX.Element {
  const session = useQuery(sessionQuery);
  const [mobileOpen, setMobileOpen] = useState(false);
  const queryClient = useQueryClient();
  const location = useLocation();

  const switchOrganization = useMutation({
    mutationFn: (organizationId: string) =>
      apiRequest('/auth/organization', CurrentSessionSchema, {
        method: 'POST',
        body: JSON.stringify(SwitchOrganizationRequestSchema.parse({ organizationId })),
      }),
    onSuccess: async (updated) => {
      queryClient.setQueryData(['session'], updated);
      await queryClient.invalidateQueries();
    },
  });
  const logout = useMutation({
    mutationFn: () =>
      apiRequest('/auth/logout', CurrentSessionSchema.partial(), { method: 'POST' }),
    onSuccess: () => {
      queryClient.clear();
      window.location.assign('/login');
    },
  });

  if (session.isPending) return <LoadingState label="Loading your session" />;
  if (session.error instanceof ApiError && session.error.status === 401) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  if (session.isError)
    return (
      <Box sx={{ p: 4 }}>
        <ErrorState error={session.error} retry={() => void session.refetch()} />
      </Box>
    );
  const current = session.data;

  // A disabled area is unreachable, not merely unlisted. Deep links and browser
  // history land back on the dashboard rather than rendering a page the client
  // did not contract for.
  const requested = navigation.find(
    (item) => item.to !== '/' && location.pathname.startsWith(item.to),
  );
  if (requested && !isEnabled(current, requested)) {
    return <Navigate to="/" replace />;
  }

  const drawer = (
    <Box
      component="nav"
      aria-label="Primary navigation"
      sx={{ height: '100%', bgcolor: brand.primaryDarker, color: '#fff' }}
    >
      <Toolbar>
        {/* The logo is the usual way back to the home page, so make it one. */}
        <Box
          component={NavLink}
          to="/"
          aria-label="Suncoast Communities home"
          sx={{ display: 'block', lineHeight: 0, borderRadius: 1 }}
        >
          <Box
            component="img"
            src={logoLight}
            alt="Suncoast Communities"
            sx={{ width: 168, maxWidth: '100%', display: 'block' }}
          />
        </Box>
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(255,255,255,.14)' }} />
      <List sx={{ px: 1 }}>
        {(Object.keys(GROUP_TITLES) as NavigationGroup[]).flatMap((group) => {
          const items = navigation.filter(
            (item) =>
              item.group === group &&
              hasPermission(current, item.permission) &&
              isEnabled(current, item),
          );
          const title = GROUP_TITLES[group];
          if (items.length === 0) return [];
          return [
            title ? (
              <ListSubheader
                key={`group-${group}`}
                disableSticky
                sx={{
                  bgcolor: 'transparent',
                  color: 'rgba(255,255,255,.5)',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  lineHeight: 2.6,
                  mt: 1,
                }}
              >
                {title}
              </ListSubheader>
            ) : null,
            ...items.map((item) => (
              <ListItem key={item.to} disablePadding>
                <ListItemButton
                  component={NavLink}
                  to={item.to}
                  end={item.to === '/'}
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.5,
                    color: 'rgba(255,255,255,.82)',
                    '&.active': { bgcolor: 'rgba(255,255,255,.14)', color: '#fff' },
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            )),
          ];
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            aria-label="Open navigation"
            onClick={() => setMobileOpen(true)}
            sx={{ display: { md: 'none' }, mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            {current.organizations.length > 1 ? (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="organization-label">Organization</InputLabel>
                <Select
                  labelId="organization-label"
                  label="Organization"
                  value={current.activeOrganizationId}
                  onChange={(event) => switchOrganization.mutate(event.target.value)}
                >
                  {current.organizations.map((organization) => (
                    <MenuItem key={organization.id} value={organization.id}>
                      {organization.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}
            <Typography sx={{ display: { xs: 'none', sm: 'block' } }}>
              {current.identity.displayName}
            </Typography>
            <Button onClick={() => logout.mutate()} disabled={logout.isPending}>
              Sign out
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>
      <Box component="aside" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, sm: 3, lg: 4 } }}
      >
        <Toolbar />
        <Outlet context={{ session: current }} />
      </Box>
    </Box>
  );
}
